import {AppModule} from "@/store/modules/app";
import {IPointGeometry, IResult} from "@/types/map";
import axios from "axios";
import {loadModules} from "esri-loader";
import WallLayer from "../../Animation/WallLayer";
import {checkPoints} from "./CheckPoints";

export default class PassagewayView {
  private static intances: Map<string, PassagewayView>;

  public static getInstance(view: __esri.SceneView) {
    const id = view.container.id;
    if (!PassagewayView.intances) {
      PassagewayView.intances = new Map();
    }
    let instance = PassagewayView.intances.get(id);
    if (!instance) {
      instance = new PassagewayView(view);
      PassagewayView.intances.set(id, instance);
    }
    return instance;
  }

  private view!: __esri.SceneView;

  private initialized = false;
  private passagewayLayer!: __esri.FeatureLayer;
  private highlightLayer!: __esri.FeatureLayer;
  private Graphic!: __esri.GraphicConstructor;
  private roadGraphics: Array<__esri.Graphic> = [];
  private highlightCrossGraphics: Array<__esri.Graphic> = [];

  private wallLayer!: WallLayer;

  private constructor(view: __esri.SceneView) {
    this.view = view;
  }

  public async initialize() {
    if (!this.initialized) {
      type MapModules = [
        typeof import("esri/Graphic"),
        typeof import("esri/layers/FeatureLayer")
      ];
      const [Graphic, FeatureLayer] = await (loadModules([
        "esri/Graphic",
        "esri/layers/FeatureLayer"
      ]) as Promise<MapModules>);

      this.Graphic = Graphic;

      // MapServer不能Update，所以先用featureLayer获取graphics，再本地创建FeatureLayer
      const sourceLayer = new FeatureLayer({
        url: `http://${AppModule.appConfig.gisServer}/arcgis/rest/services/Unicron/ShangHai_Road/MapServer/8`,
        outFields: ["*"]
      });

      this.roadGraphics = (
        await sourceLayer.queryFeatures(sourceLayer.createQuery())
      ).features;

      this.passagewayLayer = new FeatureLayer({
        source: this.roadGraphics,
        objectIdField: "OBJECTID",
        geometryType: "polyline",
        definitionExpression: "Flow > 0",
        visible: false,
        spatialReference: {wkid: 4326},
        fields: [
          {name: "OBJECTID", type: "oid"},
          {name: "FEATUREID", type: "string"},
          {name: "NAME", type: "string"},
          {name: "TYPE", type: "string"},
          {name: "ROADID", type: "string"},
          {name: "FNode", type: "string"},
          {name: "TNode", type: "string"},
          {name: "Flow", type: "double"}
        ],
        popupTemplate: {
          title: "{NAME}",
          content: "流量: {Flow}"
        },
        renderer: {
          type: "simple",
          symbol: {
            type: "simple-line",
            color: "lightblue",
            width: "5px",
            cap: "butt"
          },
          visualVariables: [
            {
              type: "color",
              field: "Flow",
              stops: [
                {value: 100, color: "#4caf50"},
                {value: 500, color: "#cddc39"},
                {value: 5000, color: "#e65100"}
              ]
            }
          ]
        } as any
      });

      this.highlightLayer = new FeatureLayer({
        source: [],
        objectIdField: "OBJECTID",
        geometryType: "point",
        fields: [
          {name: "OBJECTID", type: "oid"},
          {name: "id", type: "string"},
          {name: "name", type: "string"},
          {name: "crossType", type: "string"}
        ],
        renderer: {
          type: "unique-value",
          field: "crossType",
          defaultSymbol: {
            type: "point-3d",
            symbolLayers: [
              {
                type: "icon",
                size: "42px",
                resource: {href: "icon/discordant-cross.png"}
              }
            ]
          },
          uniqueValueInfos: [
            {
              value: "start",
              symbol: {
                type: "picture-marker",
                url: "icon/icon_start.png",
                width: "57px",
                height: "82px",
                yoffset: "41px"
              }
            },
            {
              value: "end",
              symbol: {
                type: "picture-marker",
                url: "icon/icon_end.png",
                width: "57px",
                height: "82px",
                yoffset: "41px"
              }
            }
          ]
        } as any,
        labelingInfo: [
          {
            symbol: {
              type: "text",
              color: "#e3f2fd",
              haloColor: "#0d47a1",
              haloSize: 1,
              font: {
                size: 14
              }
            } as any,
            labelExpressionInfo: {
              expression: "$feature.name"
            }
          }
        ]
      });
      this.view.map.addMany([this.passagewayLayer, this.highlightLayer]);

      this.view.on("click", () => {
        this.clearHighlight();
      });

      this.wallLayer = WallLayer.getInstance(this.view);

      this.initialized = true;
    }
  }

  public async showPassageways() {
    if (!this.initialized) {
      this.initialize();
    }

    this.roadGraphics.forEach((graphic) => (graphic.attributes["Flow"] = 0));
    await this.passagewayLayer.applyEdits({
      updateFeatures: this.roadGraphics
    });

    await this.getPassagewayData();
    this.passagewayLayer.visible = true;
  }

  private async getPassagewayData() {
    const response = await axios.get(
      "http://128.64.151.249:8000/utsc-monitor-api/sce/monitor/passageway/getAllPassagewayPoint?type=ZGF"
    );
    if (response.data.code === 0) {
      response.data.data.forEach((passagewayData: any) => {
        const segments: Array<{droadId: string; flow: number}> =
          passagewayData.droad;
        segments.forEach((segment) => {
          const segId = segment.droadId;
          const crossIds = segId.split("_");
          const crossId1 = crossIds[0];
          const crossId2 = crossIds[1];
          for (let roadGraphic of this.roadGraphics) {
            if (
              (roadGraphic.attributes["P_FNode"] === crossId1 &&
                roadGraphic.attributes["P_TNode"] === crossId2) ||
              (roadGraphic.attributes["N_FNode"] === crossId2 &&
                roadGraphic.attributes["N_TNode"] === crossId1)
            ) {
              roadGraphic.attributes["Flow"] = Math.round(
                roadGraphic.attributes["Flow"] + Number(segment.flow)
              );
              break;
            }
          }
        });
      });

      await this.passagewayLayer.applyEdits({
        updateFeatures: this.roadGraphics
      });
    }
  }

  public hidePassageways() {
    this.passagewayLayer.visible = false;
  }

  public async setPassagewayCenter(
    id: string,
    scaleFactor?: number
  ): Promise<IResult> {
    await this.clearHighlight();

    const crossIds = id.split("_");
    const crossPoints: Array<IPointGeometry> = [];
    const routePoints: Array<IPointGeometry> = [];

    crossIds.forEach((crossId, index) => {
      const crossInfo = checkPoints.find(
        (checkPoint) => checkPoint.type === "cross" && checkPoint.id === crossId
      );
      if (crossInfo) {
        crossPoints.push({
          x: crossInfo.position[0],
          y: crossInfo.position[1]
        });

        const crossGrahpic = new this.Graphic({
          geometry: {
            type: "point",
            x: crossInfo.position[0],
            y: crossInfo.position[1],
            spatialReference: {wkid: 4326}
          } as any,
          attributes: {
            name: crossInfo.name,
            id: crossInfo.id,
            crossType:
              index === 0
                ? "start"
                : index === crossIds.length - 1
                ? "end"
                : "normal"
          }
        });
        this.highlightCrossGraphics.push(crossGrahpic);
      }
    });
    await this.highlightLayer.applyEdits({
      addFeatures: this.highlightCrossGraphics
    });

    for (let i = 0; i < crossIds.length - 1; i++) {
      const id1 = crossIds[i];
      const id2 = crossIds[i + 1];
      const geometry = await this.getRoadGeometry(id1, id2);
      if (geometry) {
        const path = geometry.paths[0];
        path.forEach((point) => {
          routePoints.push({
            x: point[0],
            y: point[1]
          });
        });
      }
    }

    this.wallLayer.addWalls({
      overlays: [{geometry: {path: routePoints}}]
    });

    await this.view.goTo(this.highlightCrossGraphics);

    return {status: 0, message: "success"};
  }

  private async clearHighlight() {
    if (this.highlightCrossGraphics.length > 0) {
      await this.highlightLayer.applyEdits({
        deleteFeatures: this.highlightCrossGraphics
      });
      this.highlightCrossGraphics = [];
    }

    if (this.wallLayer) {
      this.wallLayer.deleteAllWalls();
    }
  }

  private async getRoadGeometry(id1: string, id2: string) {
    const query = this.passagewayLayer.createQuery();
    query.where = `FNode like '%${id1}%' AND TNode like '%${id2}%'`;
    query.returnGeometry = true;
    const features = (await this.passagewayLayer.queryFeatures(query)).features;
    if (features.length > 0) {
      return features[0].geometry as __esri.Polyline;
    } else {
      return undefined;
    }
  }
}
